import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, output } from '@angular/core';
import { DataState } from '../../../model/dataState';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { Verification } from '../../../services/verification';
import { NavStateService } from '../../../services/nav-state.service';
import { ActivatedRoute, Router } from '@angular/router';
import { VerificationPatchService } from '../../../services/verification-patch-service';
import { SuccessDialog } from '../../common-features/success-dialog/success-dialog';
import { MatDialog } from '@angular/material/dialog';
interface PaginationParams {
  page: number;
  size: number;
  status: string;
  requestorName: string;
  type: string;
  companyName: string
}
export interface TransferPayload {
  record: any;
  data: any;
}
@Component({
  selector: 'app-verification-list',
  standalone: false,
  templateUrl: './verification-list.html',
  styleUrl: './verification-list.less',
})

export class VerificationList implements OnInit, OnDestroy {
  private verificationService = inject(Verification);
  private navState = inject(NavStateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private matDialog = inject(MatDialog);
  pagination$ = new BehaviorSubject<PaginationParams>({ page: 0, size: 10, status: '', requestorName: '', type: '', companyName: '' });
  @Output() editVerificationItem = new EventEmitter<any>();
  @Output() createVerificationRecord = new EventEmitter<any>();
  @Output() linkToExistingRecord = new EventEmitter<any>();
  @Output() canonSearch = new EventEmitter<any>();
  @Input() userType: string = '';
  private transferService = inject(VerificationPatchService);
  private subStream!: Subscription;
  record: any;
  searchTerm: string = '';
  selectedStatus: string = 'All Statuses';
  selectedDateRange: string = 'Last 30 Days';
  verificationList$!: Observable<DataState<any[]>>;
  currentPage: number = 0;
  pageSize = 10;
  sortOrder: string = '';
  sortKey: string = '';
  totalPages = 0;
  totalRecords: number = 0;
  start: number = 0;
  end: number = 0;
  pages: any[] = [];
  stats = {
    pending: 34,
    inReview: 18,
    completed: 195,
    spend: 4850
  };


  constructor(

  ) { }

  ngOnInit(): void {
    this.navState.setCurrentTitle('Verification List');
    this.navState.setShowBackButton(false);
    const createdCompany = history.state.company;
    console.log('create company :', createdCompany);
    this.verificationList$ = combineLatest([
      this.pagination$,
      this.transferService.activeRecord$
    ]).pipe(
      switchMap(([params, activeRecord]) => {
        this.currentPage = Number(params.page) || 0;
        this.pageSize = Number(params.size) || 10;

        return this.verificationService.getVerificationList(
          params.page * params.size,
          params.size,
          params.status,
          params.requestorName,
          params.type,
          params.companyName
        ).pipe(
          tap((response: any) => {
            if (response) {
              this.totalRecords = response.total;
              this.totalPages = Math.ceil(response.total / this.pageSize);
              this.pages = Array.from({ length: this.totalPages }, (_, i) => i);
              this.start = this.currentPage * this.pageSize + 1;
              this.end = Math.min((this.currentPage + 1) * this.pageSize, this.totalRecords);

              this.record = response.result;

              // If a new activeRecord emitted through the BehaviorSubject
              if (activeRecord) {
                const { record, data } = activeRecord;
                const targetRecord = record || this.record;
                this.linkNewCompanyToExistingCompany(targetRecord, data);
                this.transferService.clearRecord();
              }
            }
          }),
          map((response: any) => ({
            loading: false,
            error: null,
            data: response.result
          })),
          catchError((error) => of({ loading: false, error: error, data: null })),
          startWith({ loading: true, error: null, data: null })
        );
      })
    );

    this.subStream = this.transferService.activeRecord$.subscribe({
      next: (payload: TransferPayload | null) => {
        if (payload && payload.record && payload.data) {
          console.log('Patching record from service payload:', payload);

          // Use payload.record directly instead of this.record
          // this.patchRequest(payload.record, payload.data,createdCompany);
          this.linkNewCompanyToExistingCompany(payload.record, payload.data);
          // Clear after consumption so it won't re-trigger 
          this.transferService.clearRecord();
        }
      }
    });

  }

  linkNewCompanyToExistingCompany(record: any, result: any) {
    this.verificationService.linkToExistingCompany(record.id, result.id)
      .subscribe({
        next: (response: any) => {
          console.log(response);
          this.pagination$.next({
            ...this.pagination$.value,
            page: 0,
            size: this.pageSize,
          });
          this.showSuccessAndErrorDialog('SUCCESS', 'Record mapped successfully!', 'success');
        },
        error: (err: any) => {
          console.log(err);
          this.showSuccessAndErrorDialog('Error', 'Could not map the record. Please contact admin.', 'error');
        }
      });
  }

  patchRequest(record: any, result: any, createdCompany?: any) {
    console.log('Target record:', record);
    const currentRecord = record;
    console.log('Target record:', currentRecord);
    console.log('Selected canon record:', result);
    if (!currentRecord) {
      console.error('No target record specified for patch request.');
      return;
    }
    console.log('created company details in patch request', createdCompany);
    const form = {
      ...currentRecord,
      status: 'SUCCESS',
      companyId: result != '' ? result.id : createdCompany.id,
      creditRecommendation: result != '' ? result.creditRecommendation : createdCompany.companyCreditRecommendation,
      riskRating: result != '' ? result.riskRating : createdCompany.companyRiskRating

    };
    console.log(form);
    this.verificationService.patchVerificationRequest(currentRecord.id, form)
      .subscribe({
        next: (response: any) => {
          console.log(response);
          this.pagination$.next({
            ...this.pagination$.value,
            page: 0,
            size: this.pageSize,
          });
          this.showSuccessAndErrorDialog('SUCCESS', 'Record mapped successfully!', 'success');
        },
        error: (err: any) => {
          console.log(err);
          this.showSuccessAndErrorDialog('Error', 'Could not map the record. Please contact admin.', 'error');
        }
      });
  }


  startVerificationWorkflow(): void {
    console.log('Routing execution step to Company Details Form component view...');
    this.router.navigate(['verification-form'], {
      relativeTo: this.route,
      state: { mode: 'create' }
    })
  }

  downloadReport(recordId: string): void {
    console.log(`Downloading compiled evaluation sheet or raw certificate for execution packet ${recordId}`);
  }

  onPageChange(newPage: number) {
    if (newPage < 0 || newPage > this.totalPages) {
      return;
    }
    this.pagination$.next({
      ...this.pagination$.value,
      page: newPage,
      size: this.pageSize,

    })
  }

  onStatusChange(status: string) {
    this.pagination$.next({
      ...this.pagination$.value,
      status: status

    })
  }

  onStatusUpdate(record: any, status: any) {
    console.log('Updating status to:', status);
    const form = {
      ...record,
      status: status,
      requestStatus: status
    };
    this.verificationService.patchVerificationRequest(record.id, form)
      .subscribe({
        next: (response: any) => {
          console.log('Status updated successfully:', response);
          this.pagination$.next({
            ...this.pagination$.value
          });
          this.showSuccessAndErrorDialog('SUCCESS', 'Status updated successfully', 'success');
        },
        error: (err: any) => {
          console.error('Error updating status:', err);
          this.showSuccessAndErrorDialog('Error', 'Could not update status. Please contact admin.', 'error');
        }
      });
  }

  onUserTypeChange(userType: string) {
    this.pagination$.next({
      ...this.pagination$.value,
      type: userType

    })
  }

  editVerificationForm(record: any) {
    console.log(record);
    this.editVerificationItem.emit(record)
  }


  mapCofanetForm(record: any) {
    this.record = record;
    this.canonSearch.emit(record);
    console.log(record)

  }

  createVerificationRecorForCompany(record: any) {
    this.createVerificationRecord.emit(record);
  }

  linkToExistingCompany(record: any) {
    this.record = record;
    this.linkToExistingRecord.emit(record);
  }

  ngOnDestroy(): void {
    if (this.subStream) {
      this.subStream.unsubscribe();
    }
  }

  onCheckboxToggle(event: any, record: any) {
    const isChecked = (event.target as HTMLInputElement).checked;
    console.log('Checkbox state changed to:', isChecked);

    const form = {
      ...record,
      queuedForAudit: true
    }

    this.verificationService.patchVerificationRequest(record.id, form)
      .subscribe({
        next: (response: any) => {
          console.log(response);
          this.showSuccessAndErrorDialog('SUCCESS', 'Record sent to audit successfully!', 'success');
        },
        error: (err: any) => {
          console.log(err);
          this.showSuccessAndErrorDialog('Error', 'Could not send the record for audit. Please contact admin.', 'error');
        }
      })
  }

  showSuccessAndErrorDialog(title: any, message: any, icon: any) {
    const dialog = this.matDialog.open(SuccessDialog, {
      disableClose: true,
      width: '500px',
      data: {
        title: title,
        subtitle: message,
        icon: icon
      },
    })
    dialog.afterClosed().subscribe(result => {

    })
  }


  onCompanyNameChange(companyName: string) {
    console.log(companyName);
    this.pagination$.next({
      ...this.pagination$.value,
      companyName: companyName
    })
  }
}